import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import styles from "./Login.module.css";
import { Link, useNavigate } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider, facebookProvider } from "../../../config/firebase";
import logo from "../../../assets/Sankrutilogo.jpeg";

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [requireOtp, setRequireOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [isPhoneMode, setIsPhoneMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userInfoString = localStorage.getItem("userInfo");

    if (token && userInfoString) {
      try {
        const userInfo = JSON.parse(userInfoString);
        if (userInfo.role === 'admin') navigate('/admin');
        else if (userInfo.role === 'security') navigate('/security');
        else navigate('/resident');
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
  }, [navigate]);

  const loginSuccess = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("userInfo", JSON.stringify(data));
    localStorage.setItem("user", JSON.stringify(data));
    
    if (data.role === 'admin') navigate('/admin');
    else if (data.role === 'security') navigate('/security');
    else if (data.role === 'superadmin') navigate('/superadmin');
    else navigate('/resident');
  };

  const handleFirebaseLogin = async (provider, providerName) => {
    if (!auth) {
      toast.error(`Firebase is not configured. Please add VITE_FIREBASE_API_KEY to your .env file.`);
      return;
    }
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      
      const res = await fetch(`${import.meta.env.VITE_API_URL}/auth/firebase-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, provider: providerName }),
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Successfully logged in with ${providerName}!`);
        loginSuccess(data);
      } else {
        toast.error(data.message || `${providerName} Login failed`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let url, body;

    if (isPhoneMode) {
      if (!requireOtp) {
        url = `${import.meta.env.VITE_API_URL}/auth/send-phone-otp`;
        body = { phone };
      } else {
        url = `${import.meta.env.VITE_API_URL}/auth/verify-phone-otp`;
        body = { phone, otp };
      }
    } else {
      if (!requireOtp) {
        url = !isLogin ? `${import.meta.env.VITE_API_URL}/auth/register` : `${import.meta.env.VITE_API_URL}/auth/login`;
        body = !isLogin ? { fullName, email, password } : { email, password };
      } else {
        url = `${import.meta.env.VITE_API_URL}/auth/verify-login-otp`;
        body = { email, otp };
      }
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requireOtp) {
          toast.success(data.message || "OTP sent to your email!");
          setRequireOtp(true);
          setLoading(false);
          return;
        }

        toast.success(isLogin ? "Successfully logged in!" : "Account created successfully!");
        loginSuccess(data);
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      {/* Background decorations matching the image */}
      <div className={styles.bgLeavesTopRight}></div>
      <div className={styles.bgLeavesBottomLeft}></div>

      <div className={styles.loginCard}>
        {/* Logo Section */}
        <div className={styles.logoContainer}>
          <img src={logo} alt="Sanskruti Shubhangan" className={styles.logo} />
        </div>

        <div className={styles.authTabs}>
          <button
            className={`${styles.authTab} ${isLogin ? styles.activeTab : ""}`}
            onClick={() => setIsLogin(true)}
          >
            {isLogin ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            ) : null}
            Login
          </button>
          <button
            className={`${styles.authTab} ${!isLogin ? styles.activeTab : ""}`}
            onClick={() => setIsLogin(false)}
          >
            {!isLogin ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
            ) : null}
            Register
          </button>
        </div>

        <div className={styles.cardHeader}>
          <h2 className={styles.title}>{isLogin ? "Welcome Back!" : "Create Account"}</h2>
          <p className={styles.subtitle}>{isLogin ? (requireOtp ? "Enter the OTP sent to your email" : "Login to access your community dashboard") : "Join our community today"}</p>
        </div>

        <form className={styles.loginForm} onSubmit={handleSubmit}>
          {isPhoneMode ? (
            <div className={styles.formGroup}>
              <label>Phone Number</label>
              <div className={styles.inputWrapper}>
                <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                <input type="tel" placeholder="+91 9876543210" className={styles.inputField} required={isPhoneMode && !requireOtp} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={requireOtp} />
              </div>
            </div>
          ) : (
            <>
              {!isLogin && (
                <div className={styles.formGroup}>
                  <label>Full Name</label>
                  <div className={styles.inputWrapper}>
                    <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    <input type="text" placeholder="Enter your full name" className={styles.inputField} required={!isLogin && !isPhoneMode} value={fullName} onChange={(e) => setFullName(e.target.value)} />
                  </div>
                </div>
              )}

              <div className={styles.formGroup} style={{ display: requireOtp ? 'none' : 'block' }}>
                <label>Email Address</label>
                <div className={styles.inputWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  <input type="email" placeholder="Enter email address" className={styles.inputField} required={!requireOtp && !isPhoneMode} value={email} onChange={(e) => setEmail(e.target.value)} />
                  {!isLogin && email.includes('@') && <svg className={styles.validIcon} viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>}
                </div>
              </div>

              <div className={styles.formGroup} style={{ display: requireOtp ? 'none' : 'block' }}>
                <label>Password</label>
                <div className={styles.inputWrapper}>
                  <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  <input type={showPassword ? "text" : "password"} placeholder="••••••••" className={styles.inputField} required={!requireOtp && !isPhoneMode} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {requireOtp && (
            <div className={styles.formGroup}>
              <label>Enter OTP</label>
              <div className={styles.inputWrapper}>
                <svg viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                <input type="text" placeholder="123456" className={styles.inputField} required value={otp} onChange={(e) => setOtp(e.target.value)} />
              </div>
            </div>
          )}

          <div className={styles.formOptionsContainer}>
            {isLogin ? (
              <div className={styles.formOptions} style={{ justifyContent: 'flex-end' }}>
                <Link to="/forgot-password" className={styles.forgotLink}>Forgot Password?</Link>
              </div>
            ) : null}

            <div className={styles.formOptions} style={isLogin ? { marginTop: '0.5rem' } : { marginTop: '0.8rem' }}>
              <label className={styles.rememberMe}>
                <input type="checkbox" className={styles.checkbox} required />
                <span>
                  I agree to the <Link to="/privacy" className={styles.linkText}>Privacy Policy</Link> and <Link to="/terms" className={styles.linkText}>Terms & Conditions</Link>
                </span>
              </label>
            </div>
          </div>

          <button className={styles.submitBtn} disabled={loading}>
            {isPhoneMode ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            ) : isLogin ? (
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line></svg>
            ) : (
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
            )}
            {loading ? "Please wait..." : isPhoneMode ? (requireOtp ? "Verify OTP & Login" : "Send OTP via SMS") : isLogin ? (requireOtp ? "Verify & Login" : "Login Securely") : "Create Account"}
          </button>
        </form>

        <div className={styles.divider}>
          <span>Or continue with</span>
        </div>

        <div className={styles.socialAuth}>
          <button className={styles.socialBtn} type="button" onClick={() => handleFirebaseLogin(googleProvider, 'Google')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.35 11.1h-9.17v2.73h5.4c-.23 1.25-.92 2.31-1.9 3l3.05 2.37C20.5 17.5 21.5 15 21.5 12c0-.31-.03-.62-.08-.92z" fill="#4285F4" /><path d="M12.18 21.5c2.75 0 5.06-.91 6.74-2.47l-3.05-2.37c-.91.61-2.07.97-3.69.97-2.84 0-5.25-1.92-6.11-4.5H2.9v2.44C4.65 19.06 8.16 21.5 12.18 21.5z" fill="#34A853" /><path d="M6.07 13.13a5.9 5.9 0 0 1 0-3.76V6.93H2.9A9.95 9.95 0 0 0 1.8 12c0 1.62.39 3.16 1.1 4.5l3.17-2.44z" fill="#FBBC05" /><path d="M12.18 6.53c1.5 0 2.85.52 3.91 1.53l2.93-2.93C17.23 3.4 14.93 2.5 12.18 2.5 8.16 2.5 4.65 4.94 2.9 8.44l3.17 2.44c.86-2.58 3.27-4.5 6.11-4.5z" fill="#EA4335" /></svg>
            Continue with Google
          </button>

        </div>

        <div className={styles.switchAuth}>
          {isLogin && !isPhoneMode ? (
            <p>Don't have an account? <span onClick={() => { setIsLogin(false); setRequireOtp(false); }}>Register Now <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></span></p>
          ) : !isPhoneMode ? (
            <p>Already have an account? <span onClick={() => setIsLogin(true)}>Login Now <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></span></p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Login;
